/**
 * @description Address formatting for AVS-friendly display and customer form blur.
 * @dependencies None
 * @notes Street abbreviations must use word boundaries. Substring replace of
 *   "North" → "N" mutates street names (Northup → Nup). Same file is duplicated
 *   in customer-portal/src/utils/addressFormatters.js — keep them in lockstep.
 */

/**
 * Format city name to proper case
 * Capitalizes first letter of each word, handles common patterns
 * @param {string} city - City name in any case
 * @returns {string} Properly formatted city name
 * 
 * Examples:
 * "kalispell" → "Kalispell"
 * "KALISPELL" → "Kalispell"  
 * "new york city" → "New York City"
 * "ST. PAUL" → "St. Paul"
 */
function formatCityName(city) {
  if (!city) return '';
  
  // Handle special cases that should remain uppercase
  const upperCaseWords = ['II', 'III', 'IV'];
  
  // Handle special case patterns
  const specialPatterns = {
    'ST ': 'St. ',
    'ST.': 'St.',
    'MT ': 'Mt. ',
    'MT.': 'Mt.',
    'FT ': 'Ft. ',
    'FT.': 'Ft.',
  };
  
  // Convert to title case
  let formatted = city
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ') // Remove extra spaces
    .split(' ')
    .map(word => {
      // Check if word should remain uppercase
      if (upperCaseWords.includes(word.toUpperCase())) {
        return word.toUpperCase();
      }
      
      // Handle hyphenated words
      if (word.includes('-')) {
        return word
          .split('-')
          .map(part => part.charAt(0).toUpperCase() + part.slice(1))
          .join('-');
      }
      
      // Handle apostrophes (e.g., O'Brien)
      if (word.includes("'")) {
        const parts = word.split("'");
        return parts
          .map(part => part.charAt(0).toUpperCase() + part.slice(1))
          .join("'");
      }
      
      // Standard capitalization
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
  
  // Apply special patterns
  Object.entries(specialPatterns).forEach(([pattern, replacement]) => {
    formatted = formatted.replace(new RegExp(pattern, 'gi'), replacement);
  });
  
  return formatted;
}

/**
 * Validate ZIP code format
 * Accepts 5-digit or ZIP+4 format
 * @param {string} zip - ZIP code
 * @returns {boolean} True if valid ZIP format
 */
function isValidZipCode(zip) {
  if (!zip) return false;
  
  // Remove spaces and hyphens for validation
  const cleanZip = zip.toString().trim();
  
  // Accept 5-digit ZIP or ZIP+4 format (12345 or 12345-6789)
  const zipRegex = /^\d{5}(-\d{4})?$/;
  return zipRegex.test(cleanZip);
}

/**
 * Format ZIP code to standard format
 * @param {string} zip - ZIP code
 * @returns {string} Formatted ZIP code
 */
function formatZipCode(zip) {
  if (!zip) return '';
  
  const cleanZip = zip.toString().replace(/[^\d-]/g, '');
  
  // If it's already in the correct format, return as is
  if (/^\d{5}(-\d{4})?$/.test(cleanZip)) {
    return cleanZip;
  }
  
  // If it's 9 digits without hyphen, add it
  if (/^\d{9}$/.test(cleanZip)) {
    return cleanZip.slice(0, 5) + '-' + cleanZip.slice(5);
  }
  
  // Return first 5 digits if longer
  if (cleanZip.length >= 5) {
    return cleanZip.slice(0, 5);
  }
  
  return cleanZip;
}

/**
 * Format street address for consistency.
 * Abbreviates whole tokens only (USPS-style suffixes and compass words).
 * @param {string} address - Street address
 * @returns {string} Formatted address
 */
function formatStreetAddress(address) {
  if (!address) return '';

  // Whole-word only — "Northup" must not match NORTH.
  const abbreviations = {
    STREET: 'St',
    AVENUE: 'Ave',
    DRIVE: 'Dr',
    ROAD: 'Rd',
    BOULEVARD: 'Blvd',
    LANE: 'Ln',
    COURT: 'Ct',
    CIRCLE: 'Cir',
    PLACE: 'Pl',
    SQUARE: 'Sq',
    PARKWAY: 'Pkwy',
    SUITE: 'Ste',
    APARTMENT: 'Apt',
    NORTH: 'N',
    SOUTH: 'S',
    EAST: 'E',
    WEST: 'W'
  };

  let formatted = address.trim();

  Object.entries(abbreviations).forEach(([full, abbr]) => {
    const regex = new RegExp(`\\b${full}\\b`, 'gi');
    formatted = formatted.replace(regex, abbr);
  });

  return formatted;
}

/**
 * Validate that required billing fields are present
 * @param {Object} billingData - Billing data object
 * @returns {Object} Validation result with isValid flag and errors
 */
function validateBillingAddress(billingData) {
  const errors = {};
  
  // Required fields
  if (!billingData.billingAddress || billingData.billingAddress.trim().length < 5) {
    errors.billingAddress = 'Billing address is required (minimum 5 characters)';
  }
  
  if (!billingData.billingZip || !isValidZipCode(billingData.billingZip)) {
    errors.billingZip = 'Valid ZIP code is required (5 digits or ZIP+4 format)';
  }
  
  // Optional fields validation (if provided)
  if (billingData.billingCity && billingData.billingCity.trim().length < 2) {
    errors.billingCity = 'City must be at least 2 characters';
  }
  
  if (billingData.billingState && billingData.billingState.length !== 2) {
    errors.billingState = 'State must be 2-letter code (e.g., MT, CA, NY)';
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
}

/**
 * Format complete billing address for display
 * @param {Object} billingData - Billing data object
 * @returns {string} Formatted address string
 */
function formatBillingAddressDisplay(billingData) {
  const parts = [];
  
  if (billingData.billingAddress) {
    parts.push(formatStreetAddress(billingData.billingAddress));
  }
  
  const cityStateZip = [];
  if (billingData.billingCity) {
    cityStateZip.push(formatCityName(billingData.billingCity));
  }
  if (billingData.billingState) {
    cityStateZip.push(billingData.billingState.toUpperCase());
  }
  if (billingData.billingZip) {
    cityStateZip.push(formatZipCode(billingData.billingZip));
  }
  
  if (cityStateZip.length > 0) {
    parts.push(cityStateZip.join(', '));
  }
  
  return parts.join('\n');
}

export {
  formatCityName,
  isValidZipCode,
  formatZipCode,
  formatStreetAddress,
  validateBillingAddress,
  formatBillingAddressDisplay
};